import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRightIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { usePageNameContext } from "../../contexts/PageTitleContext";
import classNames from "../../utils/classNames";
import useAdminSections, { IAdminLink, IAdminSectionGroup } from "./useAdminSections";

const PILL_CLASS =
  "inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-sm text-gray-700 transition hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700";

const isMac = typeof navigator !== "undefined" && navigator.platform.toUpperCase().includes("MAC");

type SearchHit = { group: IAdminSectionGroup; link: IAdminLink };

function haystack(group: IAdminSectionGroup, link: IAdminLink) {
  return [link.label, link.description, group.name, ...link.keywords].join(" ").toLowerCase();
}

/** Every word typed has to appear somewhere in the link, so "fedex key" narrows rather than widens. */
function matches(text: string, terms: string[]) {
  return terms.every((term) => text.includes(term));
}

function GroupCard({ group }: { group: IAdminSectionGroup }) {
  const Icon = group.icon;

  return (
    <div className="rounded-lg bg-white p-5 shadow">
      <div className="flex gap-3">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900">{group.name}</h3>
          <p className="mt-0.5 text-sm text-gray-500">{group.description}</p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {group.links.map((link) => (
          <Link key={link.key} to={link.href} className={PILL_CLASS} title={link.description}>
            {link.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

function SearchResult({ hit, active }: { hit: SearchHit; active: boolean }) {
  const { group, link } = hit;

  return (
    <Link
      to={link.href}
      className={classNames(
        active ? "bg-primary-50" : "hover:bg-gray-50",
        "flex items-center gap-3 px-4 py-3 sm:px-6"
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">{group.name}</span>
          <ChevronRightIcon className="h-3 w-3 text-gray-400" aria-hidden="true" />
          <span className="font-medium text-gray-900">{link.label}</span>
          {link.kind === "setting" && (
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">setting</span>
          )}
        </div>
        <p className="mt-0.5 truncate text-sm text-gray-500">{link.description}</p>
      </div>
      <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
    </Link>
  );
}

export default function AdminSettingsHome() {
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();
  const { groups } = useAdminSections();
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInfo({
      name: "Admin Settings",
      desc: "Everything that configures your yard, parts, selling and integrations",
    });
  }, [setInfo]);

  // Ctrl/Cmd+K jumps to the search from anywhere on the page, as the hint in the box promises
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const query = search.trim().toLowerCase();

  const hits = useMemo<SearchHit[]>(() => {
    const terms = query.split(/\s+/).filter(Boolean);
    if (terms.length === 0) return [];

    // Pages and tabs first, individual settings after, so the broad match is always nearest the top
    const pills = groups.flatMap((group) =>
      group.links.filter((link) => matches(haystack(group, link), terms)).map((link) => ({ group, link }))
    );
    const settings = groups.flatMap((group) =>
      group.settings.filter((link) => matches(haystack(group, link), terms)).map((link) => ({ group, link }))
    );

    return [...pills, ...settings];
  }, [groups, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [search]);

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setSearch("");
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, hits.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && hits[activeIndex]) {
      navigate(hits[activeIndex].link.href);
    }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-end">
        <div className="relative w-full max-w-md">
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </span>
          <input
            ref={inputRef}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder={'Search settings — try "fedex" or "check in"'}
            aria-label="Search settings"
            autoComplete="off"
            className="block w-full rounded-md border-gray-300 bg-white pl-10 pr-16 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
          <span className="absolute inset-y-0 right-0 flex items-center pr-2">
            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="rounded p-1 text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Clear search</span>
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            ) : (
              <kbd className="hidden rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-sans text-xs text-gray-500 sm:inline-block">
                {isMac ? "⌘K" : "Ctrl K"}
              </kbd>
            )}
          </span>
        </div>
      </div>

      {query ? (
        <div className="overflow-hidden rounded-lg bg-white shadow">
          {hits.length === 0 ? (
            <div className="px-4 py-6 text-sm text-gray-500 sm:px-6">
              No settings match "{search.trim()}". Try a different word, or browse the groups below.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {hits.map((hit, i) => (
                <SearchResult key={hit.link.key} hit={hit} active={i === activeIndex} />
              ))}
            </div>
          )}
        </div>
      ) : null}

      {/* The groups stay up under a search with no hits, so there is still somewhere to go */}
      {query && hits.length > 0 ? null : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {groups.map((group) => (
            <GroupCard key={group.key} group={group} />
          ))}
        </div>
      )}
    </div>
  );
}
